import { COLORS } from "../../../assets/utils/enums/colors";
import { MediumText } from "../../../components/typography";
import FinanceChart, {
  FinanceChartProps,
  FinanceDataItem,
} from "../../../components/ui/finance-chart";
import iconStatistic from "../../../assets/images/icons/pie-one-icon.svg";
import FinanceDonutChart, {
  DonutRawData,
} from "../../../components/ui/finance-donat-chart";
import StatisticBlock, {
  DataStatisticProps,
} from "../../../components/ui/statistics-block";
import { useStatisticPeriod } from "../../../assets/store/useStatisticPeriod";
import { Button } from "../../../components/ui/button";
import DataTable from "../../../components/ui/table";
import {
  tableColumnType,
  TableTypeEnum,
} from "../../../assets/utils/enums/table";
import { useEffect, useState } from "react";
import getData from "../../../api/project-request";
import { API_URL_FINANCE } from "../../../constants";
import { currencies } from "../../../assets/utils/data/items";
import Loader from "../../../components/ui/loader";
import { FinanceRow } from "../../../assets/utils/table-types";
import { useNavigate } from "react-router-dom";
import { FinanceTypeTable } from "../../../assets/utils/enums/finance";
import { deleteArrFunc } from "../../../assets/utils/request-function";
import { useStructureStore } from "../../../assets/store/useStructureStore";
import { ObjectsEnum } from "../../../assets/utils/enums/basket";
import { useStructureColumnsStore } from "../../../assets/store/useStructureColumnsStore";

export default function IncomeMainTab() {
  const { period, setPeriod } = useStatisticPeriod();
  const [incomeChart, setIncomeChart] = useState<FinanceChartProps>({});
  const [incomeDonatChart, setIncomeDonatChart] = useState<DonutRawData[]>([]);
  const [loaderState, setLoaderState] = useState<boolean>(false);
  const [incomesTable, setIncomesTable] = useState<FinanceRow[]>([]);
  const [mainFinStatistics, setMainFinStatistics] = useState<
    DataStatisticProps[]
  >([]);
  const navigate = useNavigate();
  const [isDeleted, setIsDeleted] = useState<boolean>(false);

  const fields = useStructureStore(
    (s) => s.structures[ObjectsEnum.INCOME].fields
  )
  const baseColumns =
    tableColumnType[TableTypeEnum.FINANCE].columns;

  const tableStructure = useStructureColumnsStore(
    baseColumns,
    fields
  );

  const getIncomesTable = async () => {
    setLoaderState(true);
    try {
      const data = await getData(`${API_URL_FINANCE}/get-table-income`);
      if (Array.isArray(data)) {
        setIncomesTable(data);
      } else {
        setIncomesTable([]);
      }
    } catch (error: any) {
      console.log(error.message);
      setIncomesTable([]);
    } finally {
      setTimeout(() => {
        setLoaderState(false);
      }, 100);
    }
  };

  useEffect(() => {
    const getIncomeChartStatistic = async () => {
      setLoaderState(true);
      try {
        const data = await getData(
          `${API_URL_FINANCE}/get-incomes-statistics?currency=${currencies[1].code}`
        );

        if (!data?.data) {
          setIncomeChart({});
          return;
        }

        setIncomeChart(data);
      } catch (error) {
        console.error("❌ Помилка при запиті статистики:", error);
        setIncomeChart({});
      } finally {
        setTimeout(() => {
          setLoaderState(false);
        }, 100);
      }
    };

    getIncomeChartStatistic();
    getIncomesTable();
  }, []);

  useEffect(() => {
    const getIncomeDonatChartStatistic = async () => {
      setLoaderState(true);
      try {
        const data = await getData(
          `${API_URL_FINANCE}/get-incomes-statistic-by-period?period=${period}`
        );

        if (!data) {
          setIncomeDonatChart([]);
          return;
        }

        setIncomeDonatChart(data);
      } catch (error) {
        console.error("❌ Помилка при запиті статистики:", error);
        setIncomeDonatChart([]);
      } finally {
        setTimeout(() => {
          setLoaderState(false);
        }, 100);
      }
    };

    const getStatisticFinance = async () => {
      setLoaderState(true);
      const data = await getData(
        `${API_URL_FINANCE}/finance-summary-all?period=${period}&currency=${currencies[1].code}`
      );

      setTimeout(() => {
        setMainFinStatistics([
          {
            title: "Надходження",
            value: data.formattedIncomes,
            percent: `${data.incomesChangePercent}`,
          },
          {
            title: "Витрати",
            value: data.formattedExpenses,
            percent: `${data.expensesChangePercent}`,
          },
        ]);
        setLoaderState(false);
      }, 100);
    };

    getIncomeDonatChartStatistic();
    getStatisticFinance();
  }, [period, isDeleted]);

  useEffect(() => {
    getIncomesTable();
    setIsDeleted(false);
  }, [isDeleted]);

  return (
    <>
      <div className="d-flex flex-column gap-3">
        <div className="d-flex w-100 gap-3">
          <FinanceChart
            year={incomeChart.year}
            currency={incomeChart.currency}
            data={incomeChart.data as FinanceDataItem[]}
            typeTable={incomeChart.typeTable}
          />
          <div className="w-40 d-flex flex-column justify-content-between gap-3">
            <StatisticBlock
              iconColor={COLORS.main1}
              iconUrl={iconStatistic}
              bgColor={COLORS.white}
              isDropdown={true}
              data={mainFinStatistics}
            />
            <FinanceDonutChart
              type={"income"}
              title={"Розподіл надходжень"}
              data={incomeDonatChart}
              period={period}
              setPeriod={setPeriod}
            />

            <Button
              bgcolor={COLORS.secondary1}
              txtcolor={COLORS.white}
              brcolor={"transparent"}
              width="auto"
              className="m-0"
              type="large"
              onClick={() => {
                navigate("/finance/income/add");
              }}
            >
              <MediumText>Додати надходження</MediumText>
            </Button>
          </div>
        </div>
        <DataTable
          table={{ ...tableColumnType[TableTypeEnum.FINANCE], columns: tableStructure }}
          data={incomesTable}
          isFinanceTitle={FinanceTypeTable.INCOME}
          deleteArrFunc={(queryArr) =>
            deleteArrFunc(queryArr, `${API_URL_FINANCE}/incomes`, setIsDeleted)
          }
        />
      </div>
      {loaderState && <Loader />}
    </>
  );
}
